import { type FormEvent, useState } from 'react'
import { ArrowLeft, Trash2 } from 'lucide-react'
import { Link } from 'react-router'

import { PageFrame } from 'shared/components/page-frame'
import { SectionTitle } from 'shared/components/section'
import { Button } from 'shared/components/ui/button'
import { Field, FieldError, FieldGroup, FieldLabel } from 'shared/components/ui/field'
import { Input } from 'shared/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from 'shared/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from 'shared/components/ui/tooltip'
import { useTranslate } from 'shared/lib/i18n'
import { DEFAULT_API_ENDPOINT, DEV_STORAGE_DEFAULTS, useStorage } from 'shared/lib/storage'
import { useAuthStore } from 'shared/store/auth'

/**
 * The developer's one setting: which server the panel talks to. Reachable signed out
 * too (`/settings` beside `/login`), so a developer points the panel at a server
 * before signing in; offered in the sidebar only in dev.
 */
export const Settings = () => {
	const { t } = useTranslate()
	const signedIn = useAuthStore((state) => state.isAuthenticated)

	const page = (
		<PageFrame
			title='settings.title'
			back={
				!signedIn && (
					<Button variant='ghost' size='icon' asChild>
						<Link to='/login' aria-label={t('settings.toLogin')} title={t('settings.toLogin')}>
							<ArrowLeft />
						</Link>
					</Button>
				)
			}
		>
			<ServerSection />
		</PageFrame>
	)

	// Signed out there is no layout around the page to give it its margin.
	return signedIn ? page : <div className='p-4'>{page}</div>
}

/**
 * The server: the saved addresses in one select, the chosen one in use. The client
 * reads the address once, when the panel starts — so a change reloads the page.
 */
function ServerSection() {
	const { t } = useTranslate()
	const [dev, setDev] = useStorage('dev', DEV_STORAGE_DEFAULTS)
	const { apiEndpoints, selectedApiEndpoint } = dev

	const [address, setAddress] = useState('')
	const [error, setError] = useState<string | null>(null)

	const switchTo = (next: typeof dev) => {
		setDev(next)
		window.location.reload()
	}

	const choose = (endpoint: string) => {
		if (endpoint !== selectedApiEndpoint) switchTo({ ...dev, selectedApiEndpoint: endpoint })
	}

	const remove = () =>
		switchTo({
			apiEndpoints: apiEndpoints.filter((endpoint) => endpoint !== selectedApiEndpoint),
			selectedApiEndpoint: DEFAULT_API_ENDPOINT,
		})

	const add = (e: FormEvent) => {
		e.preventDefault()
		const endpoint = address.trim()
		if (!URL.canParse(endpoint)) return setError(t('settings.invalidAddress'))
		if (apiEndpoints.includes(endpoint)) return setError(t('settings.addressExists'))
		switchTo({ apiEndpoints: [...apiEndpoints, endpoint], selectedApiEndpoint: endpoint })
	}

	return (
		<section className='flex max-w-xl flex-col gap-3'>
			<SectionTitle title={t('settings.server')} hint={t('settings.serverHint')} />
			<FieldGroup>
				<Field>
					<FieldLabel htmlFor='settings-endpoint'>{t('settings.address')}</FieldLabel>
					<div className='flex gap-2'>
						<Select value={selectedApiEndpoint} onValueChange={choose}>
							<SelectTrigger id='settings-endpoint' className='w-full' data-testid='settings-endpoint'>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{apiEndpoints.map((endpoint) => (
									<SelectItem key={endpoint} value={endpoint}>
										{endpoint}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Tooltip>
							<TooltipTrigger asChild>
								<span>
									<Button
										variant='outline'
										size='icon'
										onClick={remove}
										disabled={selectedApiEndpoint === DEFAULT_API_ENDPOINT}
										aria-label={t('settings.remove')}
										data-testid='settings-remove'
									>
										<Trash2 />
									</Button>
								</span>
							</TooltipTrigger>
							<TooltipContent>{t('settings.remove')}</TooltipContent>
						</Tooltip>
					</div>
				</Field>
				<Field>
					<FieldLabel htmlFor='settings-new-endpoint'>{t('settings.newAddress')}</FieldLabel>
					<form className='flex gap-2' onSubmit={add}>
						<Input
							id='settings-new-endpoint'
							value={address}
							onChange={(e) => {
								setAddress(e.target.value)
								setError(null)
							}}
							placeholder='https://'
							data-testid='settings-new-endpoint'
						/>
						<Button type='submit' variant='outline' disabled={!address.trim()}>
							{t('settings.add')}
						</Button>
					</form>
					{error && <FieldError>{error}</FieldError>}
				</Field>
			</FieldGroup>
		</section>
	)
}
